import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import { getMaxPairsInDictionary } from '../builtInDictionaries';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

interface ChatSession {
  id: number;
  title: string;
  created_at: string;
  message_count: number;
  dictionary_generated: boolean;
  generated_dictionary?: Record<string, string>;
}

interface AIDictionaryCreatorProps {
  onDictionaryGenerated: (dictionary: Record<string, string>) => void;
  onClose: () => void;
}

const AIDictionaryCreator: React.FC<AIDictionaryCreatorProps> = ({ 
  onDictionaryGenerated, 
  onClose 
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [showSessions, setShowSessions] = useState(false);
  const [usageStats, setUsageStats] = useState<any>(null);
  const [generatedDictionary, setGeneratedDictionary] = useState<Record<string, string> | null>(null);
  const [dictionaryName, setDictionaryName] = useState<string>('');
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<{sessionId: number, title: string, hasDictionary: boolean} | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  };

  const loadUsageStats = async () => {
    try {
      const stats = await apiService.getAIUsageStats();
      setUsageStats(stats);
    } catch (error) {
      console.error('Failed to load usage stats:', error);
    }
  };

  const loadChatSessions = async () => {
    try {
      const response = await apiService.getAIChatSessions(1, 10);
      setChatSessions(response.sessions);
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
    }
  };

  const createNewSession = useCallback(async () => {
    try {
      setError(null);
      const response = await apiService.createAIChatSession();
      setCurrentSession(response.session);
      setMessages([{
        id: 0,
        role: 'assistant',
        content: response.welcomeMessage,
        created_at: new Date().toISOString()
      }]);
      loadUsageStats();
    } catch (error: any) {
      setError(error.message || 'Failed to create chat session');
      console.error('Failed to create session:', error);
    }
  }, []);

  useEffect(() => {
    loadUsageStats();
    loadChatSessions();
    createNewSession();
  }, [createNewSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-hide success message after 2 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const loadSession = async (sessionId: number) => {
    try {
      setError(null);
      const response = await apiService.getAIChatSession(sessionId);
      setCurrentSession(response.session);
      setMessages(response.messages);
      setShowSessions(false);
      
      // Restore generated dictionary if available
      if (response.session.generated_dictionary) {
        setGeneratedDictionary(response.session.generated_dictionary);
        setDictionaryName(response.session.title);
        setIsEditingName(false);
      } else {
        setGeneratedDictionary(null);
        setDictionaryName('');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load chat session');
      console.error('Failed to load session:', error);
    }
  };

  const sendMessage = async () => {
    if (!currentInput.trim() && !urlInput.trim()) return;
    if (!currentSession) return;

    const userMessage: Message = {
      id: messages.length,
      role: 'user',
      content: urlInput ? `${currentInput.trim()}\n\nWebsite: ${urlInput}` : currentInput,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentInput('');
    setUrlInput('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.sendAIMessage(
        currentSession.id,
        currentInput,
        urlInput || undefined
      );

      const aiMessage: Message = {
        id: messages.length + 1,
        role: 'assistant',
        content: response.message,
        created_at: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);

      if (response.dictionary) {
        setGeneratedDictionary(response.dictionary);
        // Set default dictionary name from session title
        const defaultName = response.session?.title || currentSession?.title || 'My Custom Game';
        setDictionaryName(defaultName);
        setIsEditingName(false);
        // Update session with new title if provided by backend
        if (response.session) {
          setCurrentSession(response.session);
        } else {
          setCurrentSession(prev => prev ? { ...prev, dictionary_generated: true } : null);
        }
      }

      loadUsageStats();
    } catch (error: any) {
      setError(error.message || 'Failed to send message');
      console.error('Failed to send message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const testDictionary = () => {
    if (generatedDictionary) {
      onDictionaryGenerated(generatedDictionary);
      onClose();
    }
  };

  const saveDictionary = async () => {
    if (!generatedDictionary) return;

    try {
      // Check for duplicates by getting user's existing dictionaries
      const response = await apiService.getDictionaries();
      
      // Handle both array and object responses
      const userDictionaries = Array.isArray(response) ? response : ((response as any).dictionaries || []);
      
      // Check if this dictionary content already exists
      const duplicateDict = userDictionaries.find((dict: any) => {
        if (!dict.data) return false;
        
        // Compare dictionary content by checking if they have the same key-value pairs
        const existingKeys = Object.keys(dict.data).sort();
        const newKeys = Object.keys(generatedDictionary).sort();
        
        if (existingKeys.length !== newKeys.length) return false;
        
        return existingKeys.every(key => 
          dict.data[key] === generatedDictionary[key]
        );
      });

      if (duplicateDict) {
        setError(`This game has already been saved as "${duplicateDict.name}"`);
        return;
      }

      // Use the custom dictionary name (user can edit this)
      const finalName = dictionaryName.trim() || currentSession?.title || 'Custom AI Game';

      await apiService.createDictionary({
        name: finalName,
        description: `AI-generated game - Created on ${new Date().toLocaleDateString()}`,
        content: generatedDictionary,
        category: 'ai-generated',
        is_public: false
      });
      
      setSuccessMessage('Game saved to your profile!');
    } catch (error: any) {
      setError(error.message || 'Failed to save game');
      console.error('Failed to save game:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const startRename = (sessionId: number, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  };

  const saveRename = async (sessionId: number) => {
    try {
      await apiService.updateAIChatSession(sessionId, { title: editingTitle });
      setChatSessions(prev => prev.map(session => 
        session.id === sessionId ? { ...session, title: editingTitle } : session
      ));
      if (currentSession?.id === sessionId) {
        setCurrentSession(prev => prev ? { ...prev, title: editingTitle } : null);
      }
      setEditingSessionId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to rename session:', error);
      setError('Failed to rename conversation');
    }
  };

  const cancelRename = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const handleRenameKeyPress = (e: React.KeyboardEvent, sessionId: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveRename(sessionId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  };

  const confirmDelete = (sessionId: number, hasDictionary: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    const session = chatSessions.find(s => s.id === sessionId);
    const sessionTitle = session?.title || 'this conversation';
    
    setShowDeleteConfirm({
      sessionId,
      title: sessionTitle,
      hasDictionary
    });
  };

  const handleDeleteConfirm = () => {
    if (showDeleteConfirm) {
      deleteSession(showDeleteConfirm.sessionId);
      setShowDeleteConfirm(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  const deleteSession = async (sessionId: number) => {
    try {
      setDeletingSessionId(sessionId);
      await apiService.deleteAIChatSession(sessionId);
      
      // Remove from sessions list
      setChatSessions(prev => prev.filter(session => session.id !== sessionId));
      
      // If this was the current session, reset to new session
      if (currentSession?.id === sessionId) {
        createNewSession();
      }
      
      setDeletingSessionId(null);
    } catch (error) {
      console.error('Failed to delete session:', error);
      setError('Failed to delete conversation');
      setDeletingSessionId(null);
    }
  };

  return (
    <div className="ai-dictionary-creator">
      <div className="ai-creator-header">
        <div className="header-left">
          <h3>🤖 AI Game Creator</h3>
          <span className="new-badge">New!</span>
        </div>
        <div className="header-right">
          <button 
            className="sessions-btn"
            onClick={() => setShowSessions(!showSessions)}
          >
            📁 Sessions ({chatSessions.length})
          </button>
          <button 
            className="new-chat-btn"
            onClick={createNewSession}
            disabled={!usageStats?.canCreateChat}
          >
            ➕ New Chat
          </button>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {usageStats && (
        <div className="usage-stats">
          <span>💬 Messages: {usageStats.remaining.messages} left today</span>
          <span>🎯 Chats: {usageStats.remaining.chatSessions} left today</span>
        </div>
      )}

      {showSessions && (
        <div className="sessions-panel">
          <h4>Recent Chat Sessions</h4>
          <div className="sessions-list">
            {chatSessions.length === 0 ? (
              <p>No previous sessions</p>
            ) : (
              chatSessions.map(session => (
                <div 
                  key={session.id} 
                  className={`session-item ${session.dictionary_generated ? 'has-dictionary' : ''}`}
                  onClick={() => editingSessionId !== session.id && loadSession(session.id)}
                >
                  <div className="session-info">
                    {editingSessionId === session.id ? (
                      <input
                        type="text"
                        className="session-title-edit"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={(e) => handleRenameKeyPress(e, session.id)}
                        onBlur={() => saveRename(session.id)}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span 
                        className="session-title"
                        onDoubleClick={(e) => startRename(session.id, session.title, e)}
                      >
                        {session.title}
                      </span>
                    )}
                    <span className="session-date">{formatDate(session.created_at)}</span>
                  </div>
                  <div className="session-meta">
                    <span className="message-count">{session.message_count} messages</span>
                    {session.dictionary_generated && <span className="dict-badge">📚 Game</span>}
                    {editingSessionId !== session.id && (
                      <div className="session-actions">
                        <button 
                          className="rename-btn"
                          onClick={(e) => startRename(session.id, session.title, e)}
                          title="Rename conversation"
                        >
                          ✏️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={(e) => confirmDelete(session.id, session.dictionary_generated, e)}
                          title="Delete conversation"
                          disabled={deletingSessionId === session.id}
                        >
                          {deletingSessionId === session.id ? '⏳' : '🗑️'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <div className="chat-container">
        <div className="messages-container">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.role}`}>
              <div className="message-header">
                <span className="message-sender">
                  {message.role === 'assistant' ? '🤖 AI Assistant' : '👤 You'}
                </span>
                <span className="message-time">{formatDate(message.created_at)}</span>
              </div>
              <div className="message-content">
                {message.content.split('\n').map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message assistant loading">
              <div className="message-header">
                <span className="message-sender">🤖 AI Assistant</span>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
                Thinking...
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {generatedDictionary && (
          <div className="dictionary-preview">
            <div className="dictionary-header">
              {isEditingName ? (
                <div className="name-edit-container">
                  <input
                    type="text"
                    className="name-edit-input"
                    value={dictionaryName}
                    onChange={(e) => setDictionaryName(e.target.value)}
                    onBlur={() => setIsEditingName(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingName(false);
                      }
                      if (e.key === 'Escape') {
                        setDictionaryName(currentSession?.title || 'My Custom Game');
                        setIsEditingName(false);
                      }
                    }}
                    autoFocus
                    maxLength={100}
                  />
                  <span className="pair-count">({getMaxPairsInDictionary(generatedDictionary)} matches)</span>
                </div>
              ) : (
                <div className="name-display-container">
                  <h4>
                    📚 {dictionaryName || 'My Custom Game'} <span className="pair-count">({getMaxPairsInDictionary(generatedDictionary)} matches)</span>
                  </h4>
                  <button
                    className="edit-name-btn"
                    onClick={() => setIsEditingName(true)}
                    title="Edit game name"
                  >
                    ✏️
                  </button>
                </div>
              )}
            </div>
            <div className="dictionary-actions">
              <button className="test-btn" onClick={testDictionary}>
                🎮 Test Play
              </button>
              <button className="save-btn" onClick={saveDictionary}>
                💾 Save to Profile
              </button>
            </div>
            <div className="dictionary-sample">
              <strong>Sample matches:</strong>
              {(() => {
                const totalPairs = getMaxPairsInDictionary(generatedDictionary);
                const processedPairs = new Set<string>();
                const samplePairs: Array<[string, string]> = [];
                
                // Get first 3 unique pairs for display
                Object.entries(generatedDictionary).forEach(([key, value]) => {
                  if (samplePairs.length >= 3) return;
                  const pairId = [key, value].sort().join('|');
                  if (!processedPairs.has(pairId)) {
                    processedPairs.add(pairId);
                    samplePairs.push([key, value]);
                  }
                });
                
                return (
                  <>
                    {samplePairs.map(([key, value], i) => (
                      <span key={i} className="pair-sample">{key} ↔ {value}</span>
                    ))}
                    {totalPairs > 3 && <span>...</span>}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        <div className="input-container">
          <div className="url-input-container">
            <input
              type="url"
              className="url-input"
              placeholder="🌐 Optional: Website URL to analyze"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
            />
          </div>
          <div className="message-input-container">
            <textarea
              ref={inputRef}
              className="message-input"
              placeholder="Describe the game you want to create... (Shift+Enter for new line)"
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading || !usageStats?.canSendMessage}
              rows={1}
              style={{ resize: 'vertical', minHeight: '40px', maxHeight: '120px' }}
            />
            <button 
              className="send-btn"
              onClick={sendMessage}
              disabled={isLoading || !currentInput.trim() || !usageStats?.canSendMessage}
            >
              {isLoading ? '⏳' : '📤'}
            </button>
          </div>
        </div>
      </div>

      <div className="ai-disclaimer">
        <small>
          ⚠️ AI can make mistakes. Please review generated games for accuracy and relevance.
        </small>
      </div>

      {/* Success Message Popup */}
      {successMessage && (
        <div className="success-popup">
          <div className="success-popup-content">
            <span className="success-icon">✅</span>
            {successMessage}
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="confirm-popup-overlay">
          <div className="confirm-popup">
            <h3>Delete Conversation</h3>
            <p>Are you sure you want to delete "<strong>{showDeleteConfirm.title}</strong>"?</p>
            {showDeleteConfirm.hasDictionary && (
              <div className="warning-message">
                ⚠️ <strong>WARNING:</strong> This conversation contains a generated game that will be permanently lost if not saved to your account first.
              </div>
            )}
            <div className="confirm-popup-actions">
              <button className="cancel-btn" onClick={handleDeleteCancel}>Cancel</button>
              <button className="delete-btn" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIDictionaryCreator;